/**
 * EventBus Core Capabilities
 *
 *  1. Event Publishing
 *
 *  - publish(eventName, data?) - Asynchronously publish an event (uses setTimeout)
 *  - publishSync(eventName, data?) - Synchronously publish an event (immediate execution)
 *  - Returns true if there were subscribers, false otherwise
 *
 *  2. Event Subscriptions
 *
 *  - subscribe(eventName, callback) - Subscribe to a specific event, returns a unique token (string) for unsubscribing
 *  - subscribeAll(callback) - Subscribe to ALL events (wildcard "*")
 *  - subscribeOnce(eventName, callback) - Subscribe once, automatically unsubscribes after first execution
 *  - toggleSubscriptionBulk({action, callbacks}) - Bulk subscribe/unsubscribe multiple events at once
 *
 *  3. Unsubscribing
 *
 *  - unsubscribe(token) - By subscription token (string UID returned from subscribe)
 *  - unsubscribe(callback) - By callback function reference
 *  - unsubscribe(topic) - By topic name (clears all subscriptions under that topic)
 *  - clearAllSubscriptions() - Clear ALL subscriptions
 *  - clearSubscriptions(topic) - Clear all subscriptions for topics starting with the given string
 *
 *  4. Hierarchical Events (Topic Chains)
 *
 *  The EventBus supports dot-notation hierarchy. For example:
 *  - Publishing "user.login.success" will trigger:
 *    - Subscribers to "user.login.success"
 *    - Subscribers to "user.login" (parent topic)
 *    - Subscribers to "user" (grandparent topic)
 *    - Subscribers to "*" (wildcard)
 *
 *  5. Exception Handling
 *
 *  - Configurable via constructor: new EventBus(immediateExceptions)
 *  - true (default) - Exceptions throw immediately
 *  - false - Exceptions are thrown asynchronously (setTimeout) to avoid breaking the event chain
 *
 *  6. App ID Management
 *
 *  - setAppId(appId) / getAppId() - Optional application identifier for multi-app scenarios
 *
 *  Key Features
 *
 *  - Topic-based: Use dot notation for hierarchical events (e.g., "app.module.event")
 *  - Wildcard support: Subscribe to all events with subscribeAll()
 *  - One-time subscriptions: Auto-cleanup with subscribeOnce()
 *  - Flexible unsubscription: Multiple ways to unsubscribe
 *  - Bulk operations: Manage multiple subscriptions efficiently
 *  - Exception safety: Choose between immediate or delayed exception handling
 *
 *  Example Usage
 *
 *  const eventBus = new EventBus();
 *
 *  // Subscribe
 *  const token = eventBus.subscribe("user.login", (data) => {
 *    console.log("User logged in:", data);
 *  });
 *
 *  // Publish
 *  eventBus.publish("user.login", { username: "john" });
 *
 *  // Unsubscribe
 *  eventBus.unsubscribe(token);
 *
 *  // Subscribe to all events
 *  eventBus.subscribeAll((eventName, data) => {
 *    console.log("Any event:", eventName, data);
 *  });
 *
 *  The implementation uses a nested structure _messages[eventName][uid] = callback to store subscriptions and generates unique UIDs for each subscription.
 */

export class EventBus {
  appId: string | null = null;
  private _lastUid: number = -1;
  private _messages: { [key: string]: { [uid: string]: Function } } = {};
  private _immediateExceptions: boolean;
  private readonly _allSubscribingMsg: string = "*";

  constructor(immediateExceptions: boolean = true) {
    this._immediateExceptions = immediateExceptions;
  }
  setAppId(appId: string | null): void {
    this.appId = appId;
  }

  getAppId(): string | null {
    return this.appId;
  }

  publish(eventName: string | symbol, data?: any): boolean {
    return this._publish(eventName, data, false, this._immediateExceptions);
  }

  publishSync(eventName: string | symbol, data?: any): boolean {
    return this._publish(eventName, data, true, this._immediateExceptions);
  }

  subscribe(eventName: string, callback: Function): string {
    if (typeof callback !== "function") return "";
    const uid = `uid_${++this._lastUid}`;
    if (!this._messages[eventName]) {
      this._messages[eventName] = {};
    }
    this._messages[eventName][uid] = callback;
    return uid;
  }
  toggleSubscriptionBulk(options: { action: "subscribe" | "unsubscribe"; callbacks: { [eventName: string]: Function } }): void {
    if (options.action === "subscribe") {
      Object.entries(options.callbacks).forEach(([eventName, callback]) => {
        this.subscribe.call(this, eventName, callback);
      });
    } else {
      Object.entries(options.callbacks).forEach(([eventName, callback]) => {
        this.unsubscribe.call(this, callback);
      });
    }
  }
  subscribeAll(callback: Function): string {
    return this.subscribe(this._allSubscribingMsg, callback);
  }

  subscribeOnce(eventName: string, callback: Function): EventBus {
    const self = this;
    const uid = this.subscribe(eventName, function (...args: any[]) {
      self.unsubscribe(uid);
      callback.apply(self, args);
    });
    return this;
  }
  clearAllSubscriptions(): void {
    this._messages = {};
  }

  clearSubscriptions(topic: string): void {
    for (const msg in this._messages) {
      if (Object.prototype.hasOwnProperty.call(this._messages, msg) && msg.indexOf(topic) === 0) {
        delete this._messages[msg];
      }
    }
  }

  unsubscribe(token: string | Function): boolean {
    const isTopic = typeof token === "string" &&
      (Object.prototype.hasOwnProperty.call(this._messages, token) || this._descendantTopicExists(token));
    const isUid = !isTopic && typeof token === "string";
    const isFunction = typeof token === "function";
    let removed = false;

    if (!isTopic) {
      for (const msg in this._messages) {
        if (Object.prototype.hasOwnProperty.call(this._messages, msg)) {
          const subscribers = this._messages[msg] || {};
          if (isUid && subscribers[token as string]) {
            delete subscribers[token as string];
            removed = true;
            break;
          }
          if (isFunction) {
            for (const uid in subscribers) {
              if (Object.prototype.hasOwnProperty.call(subscribers, uid) && subscribers[uid] === token) {
                delete subscribers[uid];
                removed = true;
              }
            }
          }
        }
      }
      return removed;
    }
    this.clearSubscriptions(token as string);
    return true;
  }
  private _hasKeys(obj: any): boolean {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) return true;
    }
    return false;
  }

  private _throwException(exception: Error): () => void {
    return () => {
      throw exception;
    };
  }

  private _callSubscriberWithDelayedExceptions(callback: Function, data: any): void {
    try {
      callback(data);
    } catch (error) {
      setTimeout(this._throwException(error as Error), 0);
    }
  }

  private _callSubscriberWithImmediateExceptions(callback: Function, data: any): void {
    callback(data);
  }

  private _deliverMessage(eventName: string, topic: string, data: any, immediateExceptions: boolean): void {
    const subscribers = this._messages[topic] || {};
    const caller = immediateExceptions
      ? this._callSubscriberWithImmediateExceptions
      : this._callSubscriberWithDelayedExceptions;

    if (Object.prototype.hasOwnProperty.call(this._messages, topic)) {
      for (const uid in subscribers) {
        if (Object.prototype.hasOwnProperty.call(subscribers, uid)) {
          caller.call(this, subscribers[uid], data);
        }
      }
    }
  }
  private _createDeliveryFunction(eventName: string, data: any, immediateExceptions: boolean): () => void {
    return () => {
      let topic = String(eventName);
      let lastDotIndex = topic.lastIndexOf(".");

      this._deliverMessage(eventName, topic, data, immediateExceptions);

      while (lastDotIndex > -1) {
        topic = topic.substr(0, lastDotIndex);
        lastDotIndex = topic.lastIndexOf(".");
        this._deliverMessage(eventName, topic, data, immediateExceptions);
      }

      this._deliverMessage(eventName, this._allSubscribingMsg, data, immediateExceptions);
    };
  }
  private _hasDirectSubscribersFor(eventName: string): boolean {
    const topic = String(eventName);
    return Boolean(
      Object.prototype.hasOwnProperty.call(this._messages, topic) &&
      this._hasKeys(this._messages[topic] || {})
    );
  }

  private _messageHasSubscribers(eventName: string): boolean {
    let topic = String(eventName);
    let hasSubscribers =
      this._hasDirectSubscribersFor(topic) ||
      this._hasDirectSubscribersFor(this._allSubscribingMsg);
    let lastDotIndex = topic.lastIndexOf(".");

    while (!hasSubscribers && lastDotIndex !== -1) {
      topic = topic.substr(0, lastDotIndex);
      lastDotIndex = topic.lastIndexOf(".");
      hasSubscribers = this._hasDirectSubscribersFor(topic);
    }

    return hasSubscribers;
  }
  private _publish(eventName: string | symbol, data: any, immediate: boolean, immediateExceptions: boolean): boolean {
    const topic : string = typeof eventName === "symbol" ? eventName.toString() : eventName as any;
    const deliveryFunction = this._createDeliveryFunction(topic, data, immediateExceptions);

    if (!this._messageHasSubscribers(topic)) {
      return false;
    }

    if (immediate) {
      deliveryFunction();
    } else {
      setTimeout(deliveryFunction, 0);
    }

    return true;
  }

  private _descendantTopicExists(topic: string): boolean {
    for (const msg in this._messages) {
      if (Object.prototype.hasOwnProperty.call(this._messages, msg) && msg.indexOf(topic) === 0) {
        return true;
      }
    }
    return false;
  }
}

export default new EventBus();
